import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Col, Container, Form, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { usePatchTemplate } from "../../../hook/templates";
import { TemplateConfig, TemplateKey, TemplateNavigationPath } from "../../../models/templates";
import { templatesActions } from "../../../store/templates-slice";
import { queryClient } from "../../../utils/http/queryClient";
import { fetchTemplate, fetchTemplatesSummary, renderTemplate } from "../../../utils/http/templates";

function TemplateConfigTemplate({
  path,
  version,
  readOnly,
}: {
  path: TemplateNavigationPath;
  version: string;
  readOnly: boolean;
}) {
  const dispatch = useDispatch();

  const [possibleExtends, setPossibleExtends] = useState<TemplateKey[]>([]);
  const [feConfig, setFeConfig] = useState<TemplateConfig>({} as TemplateConfig);

  const [timeoutSaveDraftId, setTimeoutSaveDraftId] = useState<ReturnType<typeof setTimeout> | null>(null);

  const patchMutation = usePatchTemplate(path, version);

  const { data: templatesSummary } = useQuery({
    queryKey: ["templatesSummary"],
    queryFn: ({ signal }) => fetchTemplatesSummary({ signal }),
    staleTime: 2000,
  });

  const { data: template = null } = useQuery({
    queryKey: ["mailTemplates", path.namespace, path.name, version],
    queryFn: async ({ signal }) => {
      return await fetchTemplate({ signal, key: path, version: version });
    },
  });

  const { data: templateRenderReturn } = useQuery({
    queryKey: ["templateRender", path.namespace, path.name, version],
    queryFn: ({ signal }) => renderTemplate({ signal, key: path, version: version }),
    staleTime: 2000,
  });

  // const { mutate: mutateUpdateConfig } = useMutation({
  //   mutationFn: postTemplateConfig,
  //   onSuccess: async () => {
  //     console.log("Rerender");
  //     queryClient.invalidateQueries({
  //       queryKey: ["templateRender", path.namespace, path.name, version],
  //     });
  //     queryClient.invalidateQueries({
  //       queryKey: ["templateConfig", path.namespace, path.name],
  //     });
  //   },
  // });

  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: ["templateConfig", path.namespace, path.name],
    });
  }, [path]);

  useEffect(() => {
    setPossibleExtends(templatesSummary?.map((ts) => ts.key) || []);
  }, [templatesSummary]);

  useEffect(() => {
    let newConfig = {};
    if (template?.config != null) {
      newConfig = template.config;
    }
    setFeConfig({ ...(newConfig as TemplateConfig) });
  }, [template]);

  const pathToKey = (path: TemplateKey | null, humanReadable: boolean) => {
    if (path == null) return "";
    return humanReadable ? `${path.namespace} > ${path.name}` : `${path.namespace}.${path.name}`;
  };

  const keyToPathUid = (key: string): TemplateKey | null => {
    if (key === "") return null;
    return {
      namespace: key.split(".")[0],
      name: key.split(".")[1],
    };
  };

  const changeExtendsTemplate = (newExtends: TemplateKey | null) => {
    const templateBeingExtended = templatesSummary?.find(
      (ts) => newExtends?.namespace == ts.key.namespace && newExtends?.name == ts.key.name
    );

    dispatch(templatesActions.setTemplateExtendsOtherTemplate(newExtends != null));

    updateConfig({
      ...feConfig,
      extends_template: newExtends,
      show_content: templateBeingExtended?.child_conditionals_enabled || [],
    });
  };

  const updateConfig = (config: TemplateConfig) => {
    setFeConfig({ ...(config as TemplateConfig) });

    // Only send updates to the BE if nothing was changed in the past second (to avoid HTTP traffic on each keystroke)
    if (timeoutSaveDraftId) {
      clearTimeout(timeoutSaveDraftId);
    }
    const timeoutId = setTimeout(async () => {
      await patchMutation.mutateAsync({
        key: path,
        dataToUpdate: {
          "config.extends_template": config.extends_template || null,
          "config.show_content": config.show_content || null,
        },
      });

      // Make sure the preview is rerendered
      queryClient.resetQueries({
        queryKey: ["templateRender"],
      });
    }, 1000);
    setTimeoutSaveDraftId(timeoutId);
  };

  return (
    <Container className="mt-3">
      <Form.Group as={Row} className="mb-3 align-items-center">
        <Form.Label column sm="5" className="text-end">
          Extends template:
          <OverlayTrigger
            placement="bottom"
            overlay={
              <Tooltip id={`tooltip-bottom`}>
                Templates can extend other templates. Indicate which template this one extends (if any).
              </Tooltip>
            }
          >
            <i className="bi bi-info-circle ms-1"></i>
          </OverlayTrigger>
        </Form.Label>
        <Col sm="7" className="mt-2">
          <Form.Select
            value={feConfig?.extends_template ? pathToKey(feConfig.extends_template, false) : ""}
            disabled={readOnly}
            onChange={(e) => {
              changeExtendsTemplate(keyToPathUid(e.target.value));
            }}
          >
            <option value="">-- None --</option>
            {/* Make sure to cover the current extension, even if it no longer exists */}
            {feConfig?.extends_template &&
              possibleExtends.filter(
                (p) =>
                  p.namespace === feConfig?.extends_template?.namespace && p.name === feConfig?.extends_template?.name
              ).length === 0 && (
                <option
                  key={pathToKey(feConfig.extends_template, false)}
                  value={pathToKey(feConfig.extends_template, false)}
                >
                  {pathToKey(feConfig.extends_template, true)} (invalid)
                </option>
              )}
            {/* List all possibilities, except the current template */}
            {possibleExtends
              .filter((p) => !(p.namespace === path.namespace && p.name === path.name))
              .map((p) => (
                <option key={pathToKey(p, false)} value={pathToKey(p, false)}>
                  {pathToKey(p, true)}
                </option>
              ))}
          </Form.Select>
        </Col>
      </Form.Group>

      {templateRenderReturn?.child_conditionals_in_parents && (
        <Form.Group as={Row} className="mb-3 align-items-top">
          <Form.Label column sm="5" className="text-end">
            Show or hide content:
            <OverlayTrigger
              placement="bottom"
              overlay={
                <Tooltip id={`tooltip-bottom`}>
                  Templates can extend other templates. Any if-tag ending on _IN_CHILD can be enabled or disabled in
                  templates lower down the chain. Any blocks can be overwritten.
                </Tooltip>
              }
            >
              <i className="bi bi-info-circle ms-1"></i>
            </OverlayTrigger>
          </Form.Label>
          <Col sm="7" className="mt-2">
            {templateRenderReturn.child_conditionals_in_parents.sort().map((id) => (
              <Form.Check
                type="checkbox"
                disabled={readOnly}
                id={id}
                key={`show-${id}`}
                label={id}
                checked={feConfig?.show_content?.includes(id) ?? false}
                onChange={(e) => {
                  let newShowContent = feConfig?.show_content ? [...feConfig?.show_content] : [];
                  if (e.target.checked && !newShowContent.includes(id)) {
                    newShowContent.push(id);
                  }
                  if (!e.target.checked && newShowContent.includes(id)) {
                    newShowContent = newShowContent.filter((i) => i !== id);
                  }
                  updateConfig({ ...feConfig, show_content: newShowContent });
                }}
              />
            ))}
          </Col>
        </Form.Group>
      )}
    </Container>
  );
}

export default TemplateConfigTemplate;
